// Справочник как инструмент: слева человек (удалить, цвет, имя, «на плане»), справа его аккаунт
// (отвязать, имя и почта из Google, роль) — или привязка, если аккаунта нет. Аккаунты без человека —
// строки той же таблицы с пустой левой частью: привязать можно с любой стороны.
import { useMemo, useState } from 'react';
import { useAuth } from '@core/auth/store';
import { data, useTable } from '@core/store';
import { Avatar } from '@core/ui/Avatar';
import { EntitySelect } from '@core/ui/EntitySelect';
import { cn } from '@core/ui/cn';
import { GRAY, activePeople } from '@core/people';
import { ColorCell, ConfirmX, InlineText, SELECT, TD, TH } from './cells';
import {
  ON_PLAN, ON_PLAN_HINT, ON_PLAN_LABEL, ROLES, ROLE_HINT, ROLE_LABEL, adminRows, flagsOf, impactLabel, linkCandidates, newPersonDraft, onPlanOf,
  personDraftOf, personImpact, planAccount, planMerge, planNewAccountRole, planPerson, similarPeople, unlinkedAccounts,
  type OnPlan, type PeopleSort, type PeopleSortKey, type Person, type Profile, type Role, type Runner,
} from './planner';

const LEFT: { key: PeopleSortKey; label: string; title: string }[] = [
  { key: 'name', label: 'Person', title: 'Name and colour on the plan — click to edit' },
  { key: 'plan', label: 'On the plan', title: ON_PLAN_HINT },
];
const RIGHT: { key: PeopleSortKey; label: string; title: string }[] = [
  { key: 'account', label: 'Account', title: 'Who signs in as this person — name and picture come from Google' },
  { key: 'role', label: 'Role', title: ROLES.map((r) => `${ROLE_LABEL[r]} — ${ROLE_HINT[r].toLowerCase()}`).join('. ') + '.' },
];
const DIVIDER = 'border-l border-gray-200';

export function PeopleTable({ query, run, busy, adding, onAdded }: { query: string; run: Runner; busy: boolean; adding: boolean; onAdded: () => void }) {
  const people = useTable('person');
  const profiles = useTable('profile');
  const shared = useTable('shared_setting');
  const assignees = useTable('task_assignee');
  const vacations = useTable('vacation');
  const members = useTable('team_member');
  const meId = useAuth((s) => s.profile?.id ?? '');
  const [sort, setSort] = useState<PeopleSort | null>(null);
  const rows = useMemo(() => adminRows(people.values(), profiles.values(), query, sort), [people, profiles, query, sort]);
  const freeAccounts = useMemo(() => unlinkedAccounts(profiles.values()), [profiles]);
  const freePeople = useMemo(() => linkCandidates(people.values(), profiles.values()), [people, profiles]);
  const setting = useMemo(() => [...shared.values()].find((r) => r.key === 'new_account_role'), [shared]);
  const newRole = typeof setting?.value === 'string' ? setting.value : 'member';
  const toggleSort = (key: PeopleSortKey) => setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null));

  // слияние планируется по всему, что есть у человека, — дочитываем журнал и берём таблицы из стора,
  // а не из хуков: вопрос, заданный секунду назад в другом окне, иначе остался бы без автора
  const merge = async (from: Person, into: Person) => {
    await data.catchUp().catch(() => undefined);
    const t = data.store.getState().tables;
    return run(planMerge(from, into, { assignees: t.task_assignee.values(), vacations: t.vacation.values(), members: t.team_member.values(), questions: t.question.values(), answers: t.answer.values(), profiles: t.profile.values() }));
  };
  const savePerson = (p: Person, patch: Partial<ReturnType<typeof personDraftOf>>) => run(planPerson({ ...personDraftOf(p), ...patch }, p, people.values()));
  const link = (account: Profile, person_id: string | null) => run(planAccount(account, { role: account.role as Role, person_id }, meId, profiles.values()));
  const setRole = (account: Profile, role: Role) => run(planAccount(account, { role, person_id: account.person_id }, meId, profiles.values()));
  const hint = (text: string) => {
    const similar = similarPeople(text, people.values()).slice(0, 3);
    return similar.length ? (
      <span className="inline-flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-ink-muted shadow-sm whitespace-nowrap">
        already here: {similar.map((p) => <span key={p.id} className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} aria-hidden />{p.name}</span>)}
      </span>
    ) : null;
  };
  const th = (c: { key: PeopleSortKey; label: string; title: string }, extra?: string) => (
    <th key={c.key} className={cn(TH, 'cursor-pointer hover:text-ink', extra)} title={c.title}
      aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} onClick={() => toggleSort(c.key)}>
      {c.label}{sort?.key === c.key && <span aria-hidden> {sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <table className="text-sm border-collapse max-w-5xl" data-admin-people>
      <thead>
        <tr className="border-b border-gray-200">
          <th className={cn(TH, 'w-7')} aria-label="Delete person" />
          <th className={cn(TH, 'w-7')} aria-label="Colour" />
          {th(LEFT[0]!, 'w-56')}
          {th(LEFT[1]!, 'w-36')}
          <th className={cn(TH, 'w-7', DIVIDER)} aria-label="Unlink account" />
          {th(RIGHT[0]!, 'w-72')}
          {th(RIGHT[1]!, 'w-32')}
          <th className={cn(TH, 'font-normal normal-case tracking-normal text-right')}>
            <label className="inline-flex items-center gap-2 whitespace-nowrap" title="What a new account gets on its first Google sign-in, until an admin says otherwise">
              new accounts start as
              <select className={SELECT} data-admin-new-role value={newRole} disabled={busy} onChange={(e) => void run(planNewAccountRole(e.target.value as Role, setting))}>
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </label>
          </th>
        </tr>
      </thead>
      <tbody>
        {adding && (
          <tr className="border-b border-gray-100 bg-accent/5" data-admin-new-person>
            <td className={TD} />
            <td className={TD}><span className="inline-block w-3.5 h-3.5 rounded-full" style={{ background: newPersonDraft('', people.size).color }} aria-hidden /></td>
            <td className={TD} colSpan={2}>
              <InlineText value="" placeholder="New person — name, Enter to add" editing hint={hint} onCancel={onAdded} data={{ 'data-admin-new-name': '' }}
                onSave={async (name) => {
                  const ok = await run(planPerson(newPersonDraft(name, people.size), undefined, people.values()));
                  if (ok) onAdded();
                  return ok;
                }} />
            </td>
            <td className={cn(TD, DIVIDER)} colSpan={4} />
          </tr>
        )}
        {rows.map(({ key, person: p, account }) => {
          const isMe = account?.id === meId;
          return (
            <tr key={key} data-admin-person={p?.id} data-admin-account={account?.email} className={cn('group border-b border-gray-100 hover:bg-gray-50', p && !p.is_active && 'text-ink-muted')}>
              {p ? (
                <>
                  <td className={TD}>
                    <ConfirmX label={`Delete ${p.name}`} busy={busy} data={{ 'data-admin-delete': p.id }}
                      question={<>Delete {p.name}{impactLabel(personImpact(p.id, { assignees: assignees.values(), vacations: vacations.values(), members: members.values(), profiles: profiles.values() }))}?</>}
                      onConfirm={() => void run({ changes: [{ table: 'person', id: p.id, delete: true }], op: { kind: 'delete_person', name: p.name } })}
                      extra={(close) => (
                        // дубль обычно находят в момент удаления: перевесить всё на настоящего и убрать
                        <label className="inline-flex items-center gap-1 text-ink-muted border-l border-gray-200 pl-2 ml-1">or merge into
                          <EntitySelect action size="sm" className="w-40" label={`Merge ${p.name} into`} placeholder="pick a person…" disabled={busy} data={{ 'data-admin-merge': p.id }}
                            options={activePeople(people.values()).filter((x) => x.id !== p.id).map((x) => ({ id: x.id, name: x.name, color: x.color, person: true }))}
                            value={null} onChange={(id) => { const into = id ? people.get(id) : undefined; if (!into) return; close(); void merge(p, into); }} />
                        </label>
                      )} />
                  </td>
                  <td className={TD}><ColorCell value={p.color} label={p.name} onSave={(color) => void savePerson(p, { color })} /></td>
                  <td className={cn(TD, 'font-medium')}><InlineText value={p.name} data={{ 'data-admin-name': p.id }} onSave={(name) => savePerson(p, { name })} /></td>
                  <td className={TD}>
                    <select className={SELECT} data-admin-plan={p.id} value={onPlanOf(p)} disabled={busy} title={ON_PLAN_HINT}
                      onChange={(e) => void savePerson(p, flagsOf(e.target.value as OnPlan))}>
                      {ON_PLAN.map((v) => <option key={v} value={v}>{ON_PLAN_LABEL[v]}</option>)}
                    </select>
                  </td>
                </>
              ) : (
                <td className={cn(TD, 'text-ink-muted')} colSpan={4}>
                  <EntitySelect action size="sm" className="w-56" label={`Link ${account!.email} to a person`} placeholder="link to a person…" disabled={busy} data={{ 'data-admin-link': account!.email }}
                    options={freePeople.map((x) => ({ id: x.id, name: x.name, color: x.color, person: true }))} value={null} onChange={(id) => { if (id) void link(account!, id); }} />
                </td>
              )}
              {account ? (
                <>
                  <td className={cn(TD, DIVIDER)}>
                    {p && <ConfirmX label={`Unlink ${account.email}`} question="Unlink?" busy={busy} data={{ 'data-admin-unlink': p.id }} onConfirm={() => void link(account, null)} />}
                  </td>
                  <td className={TD}>
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <Avatar name={account.name || account.email} color={p?.color ?? GRAY} src={account.avatar_url} size={20} />
                      <span className="truncate">
                        {account.name && <span className="font-medium">{account.name} · </span>}{account.email}{isMe && <span className="text-ink-muted"> — you</span>}
                      </span>
                    </span>
                  </td>
                  <td className={TD} colSpan={2}>
                    <select className={SELECT} data-admin-role={account.email} value={account.role} disabled={busy || isMe}
                      title={isMe ? 'Your own role is locked — otherwise one click could leave the roadmap without an admin' : ROLE_HINT[account.role as Role]}
                      onChange={(e) => void setRole(account, e.target.value as Role)}>
                      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                    </select>
                  </td>
                </>
              ) : (
                <td className={cn(TD, DIVIDER, 'text-ink-muted')} colSpan={4}>
                  {p?.is_active ? (
                    <EntitySelect action size="sm" className="w-64" label={`Link an account to ${p.name}`} placeholder={freeAccounts.length ? 'link an account…' : 'no unlinked accounts'} disabled={busy || freeAccounts.length === 0} data={{ 'data-admin-link-account': p.id }}
                      options={freeAccounts.map((a) => ({ id: a.id, name: a.name || a.email, hint: a.name ? a.email : undefined, avatar: a.avatar_url, color: GRAY, person: true }))} value={null} onChange={(id) => { const a = id ? profiles.get(id) : undefined; if (a) void link(a, p.id); }} />
                  ) : '—'}
                </td>
              )}
            </tr>
          );
        })}
        {rows.length === 0 && !adding && <tr><td className={cn(TD, 'text-ink-muted py-3')} colSpan={8}>Nobody matches.</td></tr>}
      </tbody>
    </table>
  );
}
